import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HOME_CONTENT_MOCK } from '../../data/home-content.mock';
import type { HomeHeroContent } from '../../models/home-hero-content.model';
import { HomeHeroService } from '../../services/home-hero.service';
import { sanitizeHeroHtml } from '../../utils/home-hero-html.util';
import { AnalyticsTagService } from '../../../../services/analytics-tag.service';

export type HomeHeroPersonaType = 'natural' | 'juridica';

@Component({
  selector: 'app-home-hero',
  templateUrl: './home-hero.component.html',
  styleUrls: ['./home-hero.component.scss'],
  standalone: false,
})
export class HomeHeroComponent implements OnInit {
  content: HomeHeroContent = { ...HOME_CONTENT_MOCK.hero };

  eyebrowSafe: SafeHtml = '';
  titleSafe: SafeHtml = '';
  subtitleSafe: SafeHtml = '';

  /** Estado neutro al cargar — sin PN/PJ preseleccionado. */
  selectedPersona: HomeHeroPersonaType | null = null;
  hoverPersona: HomeHeroPersonaType | null = null;

  @Output() personaChange = new EventEmitter<HomeHeroPersonaType>();

  constructor(
    private readonly homeHeroService: HomeHeroService,
    private readonly sanitizer: DomSanitizer,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.applyContent(this.content);
  }

  ngOnInit(): void {
    this.homeHeroService.loadContent().subscribe((content) => {
      this.applyContent(content);
    });
  }

  private applyContent(content: HomeHeroContent): void {
    this.content = content;
    this.eyebrowSafe = this.toSafeHtml(content.eyebrowHtml);
    this.titleSafe = this.toSafeHtml(content.titleHtml);
    this.subtitleSafe = this.toSafeHtml(content.subtitleHtml);
  }

  private toSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(sanitizeHeroHtml(html)); // NOSONAR — whitelist en sanitizeHeroHtml
  }

  private activePersona(): HomeHeroPersonaType | null {
    return this.hoverPersona ?? this.selectedPersona;
  }

  /** Solo la "e" decorativa — no afecta fondo ni overlays. */
  isGlowEnhanced(type: HomeHeroPersonaType): boolean {
    return this.activePersona() === type;
  }

  isButtonHighlighted(type: HomeHeroPersonaType): boolean {
    return this.activePersona() === type;
  }

  onHoverPersona(type: HomeHeroPersonaType): void {
    this.hoverPersona = type;
  }

  onHoverEnd(): void {
    this.hoverPersona = null;
  }

  onSelectPersona(type: HomeHeroPersonaType): void {
    this.selectedPersona = type;
    this.trackPersonaSelection(type);
    this.personaChange.emit(type);
  }

  private trackPersonaSelection(type: HomeHeroPersonaType): void {
    const isNatural = type === 'natural';
    this.analyticsTag.trackCtaClick({
      ctaLabel: isNatural ? this.content.ctaNatural : this.content.ctaJuridica,
      ctaSection: 'hero',
      ctaComponent: 'home-hero',
      ctaElementId: isNatural ? 'btn-home-persona-natural' : 'btn-home-persona-juridica',
      hu: '8300',
    });
  }
}
